import React from 'react'
import { formatPrice } from 'shared-constants'
import { DATA_TEST_CLASS, DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled, { css } from 'styled-components'
import { ProductImage } from '@public/components/ProductImage'
import { ProductPrice } from '@public/components/ProductPrice'
import {
  getVariantsLeadTime,
  getVariantsQuantityUnit,
} from '@public/Showroom/Products/Detail/utils'
import { Flex, Grid } from 'components/Layout'
import {
  ProductDetailDescription,
  ProductOptionRow,
} from 'components/ProductOptionRow'
import { Text } from 'components/Typography'
import { type CartContextItem } from 'providers/cart'
import { useUserAuth } from 'providers/userAuth'
import { type findDiscount } from 'routes/utils/findDiscount'
import { COLOR_INTENT, FONT_SIZE, IMAGE_BASE_WIDTH, SPACE } from 'Theme'
import { formatLeadTimeString, formatQuantityWithUnit } from 'utils/formatters'

const StyledPrice = styled(Text)`
  ${({ shouldDisplayTradePrice }) =>
    shouldDisplayTradePrice &&
    css`
      text-decoration: line-through;
    `}
`

interface SummaryItemProps {
  title: string
  productImageUrl: string
  productImageAlt?: string
  discount?: ReturnType<typeof findDiscount>
  price: CartContextItem['productVariant']['price']
  tradePrice: CartContextItem['productVariant']['tradePrice']
  cartQuantity: CartContextItem['cartQuantity']
  optionValues: CartContextItem['productVariant']['optionValues']
  managedCustomFields?: CartContextItem['productVariant']['managedCustomFields']
}

export const SummaryItem: React.FC<React.PWC<SummaryItemProps>> = ({
  title,
  productImageUrl,
  productImageAlt,
  discount,
  price,
  tradePrice,
  cartQuantity,
  optionValues,
  managedCustomFields,
}) => {
  const { user } = useUserAuth()
  const leadTime = getVariantsLeadTime({
    managedCustomFields,
  })

  const shouldDisplayTradePrice = user?.isTradeCapable && tradePrice.total > 0

  return (
    <Grid
      gridTemplateColumns={`${IMAGE_BASE_WIDTH.PX_40}px 1fr max-content`}
      gridGap={SPACE.PX_12}
      data-test-id={DATA_TEST_ID.SHOWROOM_CHECKOUT_SUMMARY_PRODUCT}
    >
      <ProductImage
        m={0}
        src={productImageUrl}
        alt={productImageAlt || title}
        width={IMAGE_BASE_WIDTH.PX_40}
      />
      <Flex flexDirection="column">
        <Text>{title}</Text>
        {optionValues.map((option) => (
          <ProductOptionRow
            key={option.productOption.name}
            name={option.productOption.name}
            value={option.value}
          />
        ))}
        {leadTime && (
          <ProductDetailDescription>
            {formatLeadTimeString(leadTime)}
          </ProductDetailDescription>
        )}
        <Text
          fontsSize={FONT_SIZE.PX_13}
          data-test-class={DATA_TEST_CLASS.CHECKOUT_SUMMARY_PRODUCT_QUANTITY}
        >
          {`Qty ${formatQuantityWithUnit({
            quantity: cartQuantity,
            unit: getVariantsQuantityUnit({
              managedCustomFields,
            }),
            isUnitInParentheses: true,
          })}`}
        </Text>
      </Flex>
      <Flex flexDirection="column" alignItems="end">
        {shouldDisplayTradePrice && (
          <Text color={COLOR_INTENT.PRODUCT_HEADER.TRADE_PRICE} as="p">
            {formatPrice({
              price: tradePrice.total,
              currency: tradePrice.currencyIsoCode,
            })}
          </Text>
        )}
        <StyledPrice shouldDisplayTradePrice={shouldDisplayTradePrice}>
          {formatPrice({
            price: price.total,
            currency: price.currencyIsoCode,
          })}
        </StyledPrice>
        {discount && (
          <ProductPrice
            price={discount}
            color={COLOR_INTENT.DISCOUNT.CODE_APPLIED}
            isDiscount
          />
        )}
      </Flex>
    </Grid>
  )
}
